/**
 * Telemetry (`ProductOverview.md` §4.10).
 *
 * "What is kept is anonymous and aggregate only." This module defines exactly what that is, and
 * — just as importantly — computes it **on the client**, so the aggregates the server stores
 * were never derived from coordinates it had to understand. The server receives numbers.
 *
 * The list is closed. Adding a field here is a privacy decision, not a product one.
 */

import { haversineDistance, type LatLng } from './geo';

/** Coarse enough to be a category, never a fingerprint. */
export type DeviceClass = 'ios' | 'android' | 'desktop' | 'other';

/**
 * One device's account of one session. Every field is a count, a duration or a distance —
 * there is deliberately no session identifier, no coordinate and no precise timestamp.
 */
export interface TelemetrySummary {
  /** How long this device was in the session. */
  durationSeconds: number;
  /** How many usable position fixes it saw. */
  fixCount: number;
  /** The reported-accuracy distribution, as quantiles. Null when there were no fixes. */
  accuracyP50: number | null;
  accuracyP90: number | null;
  accuracyWorst: number | null;
  /** How close the pair actually got, and how far apart they started. */
  minDistanceMetres: number | null;
  startDistanceMetres: number | null;
  /** The north-star measurement. */
  converged: boolean;
  /** Seconds from the first shared position to convergence, if it happened. */
  secondsToConverge: number | null;
  deviceClass: DeviceClass;
  /** Whether a compass was ever available — the fallback rate matters (§6). */
  hadCompass: boolean;
}

export function deviceClassOf(userAgent: string): DeviceClass {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  if (/Windows|Macintosh|Linux|CrOS/i.test(userAgent)) return 'desktop';
  return 'other';
}

/** The p-th percentile of a sorted-on-demand sample, by nearest rank. */
export function quantile(values: number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[index] ?? null;
}

/**
 * Accumulates one device's session into the aggregates above.
 *
 * It is handed positions because that is what the client already has, and it keeps *none* of
 * them: each one is folded into a count or a distance immediately and then dropped. Reading
 * this class should make it obvious that nothing survives it that could locate anybody.
 */
export class SessionMetrics {
  private startedAt: number | null = null;
  private readonly accuracies: number[] = [];
  private fixes = 0;
  private minDistance: number | null = null;
  private startDistance: number | null = null;
  private firstPairAt: number | null = null;
  private convergedAt: number | null = null;
  private compass = false;

  constructor(private readonly deviceClass: DeviceClass) {}

  begin(now: number): void {
    this.startedAt ??= now;
  }

  /** One of our own fixes. Only its accuracy is kept, and only as a number in a list. */
  recordOwnFix(accuracyMetres: number, hadCompass: boolean, now: number): void {
    this.begin(now);
    this.fixes++;
    if (Number.isFinite(accuracyMetres)) this.accuracies.push(accuracyMetres);
    if (hadCompass) this.compass = true;
  }

  /**
   * Both positions are known, so the separation can be folded in. The positions themselves are
   * used for one subtraction and then forgotten.
   */
  recordSeparation(self: LatLng, peer: LatLng, now: number): void {
    this.begin(now);
    const metres = haversineDistance(self, peer);
    if (!Number.isFinite(metres)) return;

    this.firstPairAt ??= now;
    this.startDistance ??= metres;
    this.minDistance = this.minDistance === null ? metres : Math.min(this.minDistance, metres);
  }

  markConverged(now: number): void {
    this.convergedAt ??= now;
  }

  summarize(now: number): TelemetrySummary {
    const started = this.startedAt ?? now;
    const round1 = (value: number | null): number | null =>
      value === null ? null : Math.round(value * 10) / 10;

    return {
      durationSeconds: Math.max(0, Math.round((now - started) / 1000)),
      fixCount: this.fixes,
      accuracyP50: round1(quantile(this.accuracies, 0.5)),
      accuracyP90: round1(quantile(this.accuracies, 0.9)),
      accuracyWorst: round1(this.accuracies.length ? Math.max(...this.accuracies) : null),
      minDistanceMetres: round1(this.minDistance),
      startDistanceMetres: round1(this.startDistance),
      converged: this.convergedAt !== null,
      secondsToConverge:
        this.convergedAt === null || this.firstPairAt === null
          ? null
          : Math.max(0, Math.round((this.convergedAt - this.firstPairAt) / 1000)),
      deviceClass: this.deviceClass,
      hadCompass: this.compass,
    };
  }

  /** True once there is anything worth reporting. Empty sessions are not worth a row. */
  get isWorthReporting(): boolean {
    return this.fixes > 0 || this.minDistance !== null;
  }
}

const isFiniteOrNull = (value: unknown): value is number | null =>
  value === null || (typeof value === 'number' && Number.isFinite(value));

/**
 * Validate an inbound summary.
 *
 * Strict, and it rebuilds the object field by field rather than passing anything through: a
 * client cannot smuggle an extra property into the telemetry store by adding it to the message.
 */
export function parseTelemetrySummary(value: unknown): TelemetrySummary | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;

  const { durationSeconds, fixCount, converged, deviceClass, hadCompass } = raw;
  if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds)) return null;
  if (typeof fixCount !== 'number' || !Number.isInteger(fixCount) || fixCount < 0) return null;
  if (typeof converged !== 'boolean' || typeof hadCompass !== 'boolean') return null;
  if (
    deviceClass !== 'ios' &&
    deviceClass !== 'android' &&
    deviceClass !== 'desktop' &&
    deviceClass !== 'other'
  ) {
    return null;
  }

  const numbers = [
    'accuracyP50',
    'accuracyP90',
    'accuracyWorst',
    'minDistanceMetres',
    'startDistanceMetres',
    'secondsToConverge',
  ] as const;
  for (const key of numbers) {
    if (!isFiniteOrNull(raw[key] ?? null)) return null;
  }

  return {
    // Clamped: a bad clock or a hostile client must not skew the aggregate.
    durationSeconds: Math.min(Math.max(0, Math.round(durationSeconds)), 24 * 3600),
    fixCount: Math.min(fixCount, 100_000),
    accuracyP50: (raw['accuracyP50'] as number | null) ?? null,
    accuracyP90: (raw['accuracyP90'] as number | null) ?? null,
    accuracyWorst: (raw['accuracyWorst'] as number | null) ?? null,
    minDistanceMetres: (raw['minDistanceMetres'] as number | null) ?? null,
    startDistanceMetres: (raw['startDistanceMetres'] as number | null) ?? null,
    converged,
    secondsToConverge: (raw['secondsToConverge'] as number | null) ?? null,
    deviceClass,
    hadCompass,
  };
}
